// v10/src/components/admin/AdminSuppliers.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, Phone, Mail, Globe, ShieldCheck,
  AlertTriangle, CheckCircle, Plus, X, ExternalLink,
  MapPin, Wrench, Package, Zap, Settings, Plane,
} from 'lucide-react';
import api from '../../services/apiService';
import { useTranslation } from 'react-i18next';

// ── Helpers ───────────────────────────────────────────────────────
const TIPO_CONFIG = {
  taller:    { label: 'Taller / MRO',  icon: Wrench,   color: '#3b82f6' },
  repuestos: { label: 'Repuestos',     icon: Package,  color: '#f59e0b' },
  integral:  { label: 'Integral',      icon: Settings, color: '#8b5cf6' },
  avionica:  { label: 'Aviónica',      icon: Zap,      color: '#06b6d4' },
  motor:     { label: 'Motores',       icon: Settings, color: '#ef4444' },
  aeroclub:  { label: 'Aeroclub',      icon: Plane,    color: '#22c55e' },
};

const VerifiedBadge = ({ verified }) => {
  if (!verified)
    return (
      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
        background:'rgba(100,116,139,0.12)', color:'#64748b' }}>
        Sin verificar
      </span>
    );
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
      background:'rgba(37,99,235,0.1)', color:'#2563eb',
      display:'inline-flex', alignItems:'center', gap:4 }}>
      <ShieldCheck style={{ width:10, height:10 }} /> Verificado Condor360
    </span>
  );
};

// ── ProviderCard ──────────────────────────────────────────────────
const ProviderCard = ({ provider, darkMode, userRole, onVerify, onReview, onEdit, onDetail }) => {
  const { t } = useTranslation('admin');
  const tipo   = TIPO_CONFIG[provider.tipo] || TIPO_CONFIG.taller;
  const Icon   = tipo.icon;
  const bg     = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e2e8f0';

  return (
    <motion.div
      initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
      whileHover={{ scale:1.01, boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 4px 24px rgba(37,99,235,0.10)' }}
      onClick={() => onDetail(provider)}
      style={{ borderRadius:16, background:bg, border, padding:'16px',
        display:'flex', flexDirection:'column', gap:10, height:'100%',
        cursor:'pointer', transition:'border-color 0.2s' }}>

      {/* Content — flex:1 pushes actions to bottom */}
      <div style={{ display:'flex', flexDirection:'column', gap:10, flex:1 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:12, flexShrink:0,
          background:`${tipo.color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon style={{ width:20, height:20, color:tipo.color }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <p style={{ margin:0, fontSize:14, fontWeight:700,
              color: darkMode?'white':'#0f172a', lineHeight:1.3 }}>
              {provider.nombre}
            </p>
            <VerifiedBadge verified={provider.verified} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, flexWrap:'wrap' }}>
            <span style={{ fontSize:11, fontWeight:600, padding:'1px 7px', borderRadius:10,
              background:`${tipo.color}15`, color:tipo.color }}>
              {tipo.label}
            </span>
            {provider.habilitacion_anac && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:10,
                background:'rgba(22,163,74,0.1)', color:'#16a34a' }}>
                ANAC ✓
              </span>
            )}
            {provider.soporte_aog && (
              <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:10,
                background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>
                AOG 24h
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Ubicación */}
      {(provider.ubicacion_ciudad || provider.ubicacion_provincia) && (
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <MapPin style={{ width:13, height:13, color:'#64748b', flexShrink:0 }} />
          <span style={{ fontSize:12, color:'#64748b' }}>
            {[provider.ubicacion_ciudad, provider.ubicacion_provincia].filter(Boolean).join(', ')}
          </span>
        </div>
      )}

      {/* Descripción */}
      {provider.descripcion && (
        <p style={{ margin:0, fontSize:12, color: darkMode?'rgba(255,255,255,0.5)':'#64748b',
          lineHeight:1.5 }}>
          {provider.descripcion}
        </p>
      )}

      {/* Contacto */}
      {(provider.telefono || provider.email || provider.sitio_web) && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {provider.telefono && (
            <a href={`tel:${provider.telefono}`}
              style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#2563eb',
                textDecoration:'none', fontWeight:600 }}>
              <Phone style={{ width:12, height:12 }} /> {provider.telefono}
            </a>
          )}
          {provider.email && (
            <a href={`mailto:${provider.email}`}
              style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#2563eb',
                textDecoration:'none', fontWeight:600 }}>
              <Mail style={{ width:12, height:12 }} /> {provider.email}
            </a>
          )}
          {provider.sitio_web && (
            <a href={provider.sitio_web} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#2563eb',
                textDecoration:'none', fontWeight:600 }}>
              <Globe style={{ width:12, height:12 }} /> Sitio web
              <ExternalLink style={{ width:10, height:10 }} />
            </a>
          )}
        </div>
      )}

      {/* Corrección pendiente — visible para superadmin */}
      {provider.has_pending_correction && userRole === 'superadmin' && (
        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px',
          borderRadius:10, background:'rgba(245,158,11,0.08)',
          border:'1px solid rgba(245,158,11,0.2)' }}>
          <AlertTriangle style={{ width:12, height:12, color:'#f59e0b', flexShrink:0 }} />
          <span style={{ fontSize:11, color:'#f59e0b', fontWeight:600, flex:1 }}>
            Corrección sugerida por {provider.correction_by}
          </span>
          <motion.button onClick={() => onReview(provider.id)} whileTap={{ scale:0.97 }}
            style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:8,
              border:'none', background:'rgba(245,158,11,0.15)', color:'#f59e0b',
              cursor:'pointer', fontFamily:'inherit' }}>
            Marcar revisado
          </motion.button>
        </div>
      )}

      </div>{/* end flex:1 content */}

      {/* Acciones según rol */}
      {(userRole === 'admin' || userRole === 'instructor' || userRole === 'superadmin') && (
        <div style={{ display:'flex', gap:8, paddingTop:4,
          borderTop: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9' }}>
          {/* Verificar — solo superadmin */}
          {userRole === 'superadmin' && !provider.verified && (
            <motion.button onClick={e => { e.stopPropagation(); onVerify(provider.id); }} whileTap={{ scale:0.97 }}
              style={{ flex:1, padding:'7px', borderRadius:10, border:'none', cursor:'pointer',
                background:'rgba(37,99,235,0.08)', color:'#2563eb', fontSize:11,
                fontWeight:700, fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
              <ShieldCheck style={{ width:12, height:12 }} /> Verificar
            </motion.button>
          )}
          {/* Editar — admin, instructor y superadmin */}
          <motion.button onClick={e => { e.stopPropagation(); onEdit(provider); }} whileTap={{ scale:0.97 }}
            style={{ flex:1, padding:'7px', borderRadius:10,
              border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0',
              background:'transparent', color: darkMode?'rgba(255,255,255,0.5)':'#64748b',
              fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            {userRole === 'admin' || userRole === 'instructor' ? t('suggest_correction') : t('edit')}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

// ── ProviderModal (crear / editar) ────────────────────────────────
const ProviderModal = ({ provider, darkMode, onSave, onClose }) => {
  const isEdit = !!provider?.id;
  const [form, setForm] = useState({
    nombre:              provider?.nombre              || '',
    tipo:                provider?.tipo                || 'taller',
    descripcion:         provider?.descripcion         || '',
    ubicacion_ciudad:    provider?.ubicacion_ciudad    || '',
    ubicacion_provincia: provider?.ubicacion_provincia || '',
    telefono:            provider?.telefono            || '',
    email:               provider?.email               || '',
    sitio_web:           provider?.sitio_web           || '',
    habilitacion_anac:   provider?.habilitacion_anac   || false,
    soporte_aog:         provider?.soporte_aog         || false,
  });
  const [saving, setSaving] = useState(false);

  const bg     = darkMode ? '#1e293b' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  const inp = {
    width:'100%', padding:'9px 12px', borderRadius:10,
    background: darkMode?'rgba(255,255,255,0.06)':'#f8fafc',
    border: darkMode?'1.5px solid rgba(255,255,255,0.1)':'1.5px solid #e2e8f0',
    fontSize:13, color:text, outline:'none', fontFamily:'inherit', boxSizing:'border-box',
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/providers/${provider.id}`, form);
      else        await api.post('/api/providers', form);
      onSave();
    } catch (_) {} finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.5)',
        backdropFilter:'blur(4px)', display:'flex', alignItems:'center',
        justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.95, y:16 }} animate={{ scale:1, y:0 }}
        onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:480, borderRadius:20, background:bg, border,
          padding:24, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <p style={{ margin:0, fontSize:16, fontWeight:800, color:text }}>
            {isEdit ? 'Editar proveedor' : 'Nuevo proveedor'}
          </p>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:sub }}>
            <X style={{ width:18, height:18 }} />
          </button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>NOMBRE *</label>
            <input value={form.nombre} onChange={e=>setForm(f=>({...f,nombre:e.target.value}))} style={inp} placeholder="Nombre del proveedor" />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>TIPO *</label>
            <select value={form.tipo} onChange={e=>setForm(f=>({...f,tipo:e.target.value}))}
              style={{ ...inp, cursor:'pointer' }}>
              {Object.entries(TIPO_CONFIG).map(([k,v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>CIUDAD</label>
              <input value={form.ubicacion_ciudad} onChange={e=>setForm(f=>({...f,ubicacion_ciudad:e.target.value}))} style={inp} placeholder="Ciudad" />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>PROVINCIA</label>
              <input value={form.ubicacion_provincia} onChange={e=>setForm(f=>({...f,ubicacion_provincia:e.target.value}))} style={inp} placeholder="Provincia" />
            </div>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>TELÉFONO</label>
            <input value={form.telefono} onChange={e=>setForm(f=>({...f,telefono:e.target.value}))} style={inp} placeholder="+54 9 11 1234-5678" type="tel" />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>EMAIL</label>
            <input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={inp} placeholder="contacto@taller.com" type="email" />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>SITIO WEB</label>
            <input value={form.sitio_web} onChange={e=>setForm(f=>({...f,sitio_web:e.target.value}))} style={inp} placeholder="https://..." />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>DESCRIPCIÓN</label>
            <textarea value={form.descripcion} onChange={e=>setForm(f=>({...f,descripcion:e.target.value}))}
              style={{ ...inp, resize:'vertical', minHeight:70 }} placeholder="Servicios que ofrece..." />
          </div>
          <div style={{ display:'flex', gap:16 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:text }}>
              <input type="checkbox" checked={form.habilitacion_anac} onChange={e=>setForm(f=>({...f,habilitacion_anac:e.target.checked}))} />
              Habilitación ANAC
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:text }}>
              <input type="checkbox" checked={form.soporte_aog} onChange={e=>setForm(f=>({...f,soporte_aog:e.target.checked}))} />
              Soporte AOG 24h
            </label>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', borderRadius:12, border, background:'transparent',
              color:sub, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
            Cancelar
          </button>
          <motion.button onClick={handleSave} disabled={!form.nombre.trim() || saving}
            whileTap={{ scale:0.97 }}
            style={{ flex:2, padding:'11px', borderRadius:12, border:'none',
              background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white',
              fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear proveedor'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};


// ── ProviderDetailModal ───────────────────────────────────────────
const ProviderDetailModal = ({ provider, darkMode, userRole, onClose, onEdit, onVerify }) => {
  if (!provider) return null;
  const tipo   = TIPO_CONFIG[provider.tipo] || TIPO_CONFIG.taller;
  const Icon   = tipo.icon;
  const bg     = darkMode ? '#1e293b' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  const Row = ({ label, value, href }) => {
    if (!value) return null;
    return (
      <div style={{ display:'flex', gap:12, padding:'10px 0',
        borderBottom: darkMode?'1px solid rgba(255,255,255,0.05)':'1px solid #f1f5f9' }}>
        <span style={{ fontSize:12, fontWeight:700, color:sub, minWidth:110, flexShrink:0 }}>{label}</span>
        {href
          ? <a href={href} target="_blank" rel="noopener noreferrer"
              style={{ fontSize:13, color:'#2563eb', textDecoration:'none', fontWeight:600, wordBreak:'break-all' }}>
              {value}
            </a>
          : <span style={{ fontSize:13, color:text, wordBreak:'break-all' }}>{value}</span>
        }
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.55)',
        backdropFilter:'blur(6px)', display:'flex', alignItems:'center',
        justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.95, y:16 }} animate={{ scale:1, y:0 }} exit={{ scale:0.95, y:16 }}
        onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:480, borderRadius:22, background:bg, border,
          boxShadow:'0 24px 60px rgba(0,0,0,0.2)', maxHeight:'85vh', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ padding:'20px 20px 0', display:'flex', alignItems:'flex-start', gap:14 }}>
          <div style={{ width:48, height:48, borderRadius:14, flexShrink:0,
            background:`${tipo.color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Icon style={{ width:24, height:24, color:tipo.color }} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ margin:0, fontSize:18, fontWeight:800, color:text, lineHeight:1.3 }}>
              {provider.nombre}
            </p>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                background:`${tipo.color}15`, color:tipo.color }}>
                {tipo.label}
              </span>
              {provider.verified && (
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                  background:'rgba(37,99,235,0.1)', color:'#2563eb',
                  display:'inline-flex', alignItems:'center', gap:4 }}>
                  <ShieldCheck style={{ width:10, height:10 }} /> Verificado Condor360
                </span>
              )}
              {!provider.verified && (
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                  background:'rgba(100,116,139,0.1)', color:'#64748b' }}>
                  Sin verificar
                </span>
              )}
              {provider.habilitacion_anac && (
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                  background:'rgba(22,163,74,0.1)', color:'#16a34a' }}>ANAC ✓</span>
              )}
              {provider.soporte_aog && (
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                  background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>⚡ AOG 24h</span>
              )}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', color:sub, flexShrink:0, padding:4 }}>
            <X style={{ width:20, height:20 }} />
          </button>
        </div>

        {/* Datos */}
        <div style={{ padding:'16px 20px' }}>
          <Row label="Ciudad"     value={[provider.ubicacion_ciudad, provider.ubicacion_provincia].filter(Boolean).join(', ')} />
          <Row label="Teléfono"   value={provider.telefono}  href={provider.telefono ? `tel:${provider.telefono}` : null} />
          <Row label="Email"      value={provider.email}     href={provider.email    ? `mailto:${provider.email}` : null} />
          <Row label="Sitio web"  value={provider.sitio_web} href={provider.sitio_web} />
          <Row label="Descripción" value={provider.descripcion} />
          {provider.verified_at && (
            <Row label="Verificado" value={new Date(provider.verified_at).toLocaleDateString('es-AR')} />
          )}
        </div>

        {/* Acciones */}
        {(userRole === 'admin' || userRole === 'instructor') && (
          <div style={{ padding:'0 20px 20px', display:'flex', gap:10 }}>
            <motion.button
              onClick={() => { onClose(); onEdit(provider); }}
              whileTap={{ scale:0.97 }}
              style={{ flex:1, padding:'12px', borderRadius:14, border,
                background:'transparent', color:sub, fontSize:13, fontWeight:700,
                cursor:'pointer', fontFamily:'inherit' }}>
              Sugerir corrección
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ── AdminSuppliers ────────────────────────────────────────────────
const AdminSuppliers = ({ darkMode, userRole }) => {
  const { t } = useTranslation('admin');
  const [providers, setProviders]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterProv, setFilterProv] = useState('');
  const [filterAog,  setFilterAog]  = useState(false);
  const [filterAnac, setFilterAnac] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [showModal, setShowModal]       = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [editSaved, setEditSaved]       = useState(false);
  const [detailProvider, setDetailProvider] = useState(null);

  const bg     = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e2e8f0';
  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)      params.set('search',      search);
      if (filterTipo)  params.set('tipo',         filterTipo);
      if (filterProv)  params.set('provincia',    filterProv);
      if (filterAog)   params.set('soporte_aog',  'true');
      if (filterAnac)  params.set('habilitacion_anac', 'true');

      const res = await api.get(`/api/providers?${params}`);
      setProviders(res.data.data || []);
      if (res.data.provincias) setProvincias(res.data.provincias);
    } catch (_) {
      setProviders([]);
    } finally { setLoading(false); }
  }, [search, filterTipo, filterProv, filterAog, filterAnac]);

  useEffect(() => { load(); }, [load]);

  const handleVerify = async (id) => {
    try {
      await api.patch(`/api/providers/${id}/verify`);
      load();
    } catch (_) {}
  };

  const handleReview = async (id) => {
    try {
      await api.patch(`/api/providers/${id}/review`);
      load();
    } catch (_) {}
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditTarget(null);
    load();
    setEditSaved(true);
    setTimeout(() => setEditSaved(false), 3000);
  };

  // Agrupar por tipo para mostrar secciones
  const grouped = providers.reduce((acc, p) => {
    if (!acc[p.tipo]) acc[p.tipo] = [];
    acc[p.tipo].push(p);
    return acc;
  }, {});

  const tipoOrder = ['integral','taller','motor','avionica','repuestos','aeroclub'];
  const hasFilters = search || filterTipo || filterProv || filterAog || filterAnac;

  return (
    <div style={{ width:'100%' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:text }}>
            Directorio de Proveedores
          </h2>
          <p style={{ margin:0, fontSize:13, color:sub }}>
            {providers.length} proveedor{providers.length !== 1 ? 'es' : ''} — Argentina
          </p>
        </div>
        {(userRole === 'admin' || userRole === 'instructor') && (
          <motion.button
            onClick={() => { setEditTarget(null); setShowModal(true); }}
            whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
              borderRadius:14, border:'none', cursor:'pointer',
              background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white',
              fontSize:13, fontWeight:700, fontFamily:'inherit' }}>
            <Plus style={{ width:16, height:16 }} /> Agregar proveedor
          </motion.button>
        )}
      </div>

      {/* Badge info */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20, alignItems:'center' }}>
        <span style={{ fontSize:11, color:sub }}>Verificación:</span>
        {[
          { label:'Sin verificar',     bg:'rgba(100,116,139,0.12)', color:'#64748b' },
          { label:'Verificado Condor360', bg:'rgba(37,99,235,0.1)', color:'#2563eb' },
        ].map(b => (
          <span key={b.label} style={{ fontSize:11, fontWeight:600, padding:'2px 8px',
            borderRadius:20, background:b.bg, color:b.color }}>
            {b.label}
          </span>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:200, position:'relative' }}>
          <Search style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)',
            width:15, height:15, color:sub }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, ciudad..."
            style={{ width:'100%', padding:'9px 12px 9px 36px', borderRadius:12, border,
              background: darkMode?'rgba(255,255,255,0.05)':'#f8fafc',
              color:text, fontSize:13, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
          />
        </div>

        <select value={filterTipo} onChange={e=>setFilterTipo(e.target.value)}
          style={{ padding:'9px 12px', borderRadius:12, border, background: darkMode?'rgba(255,255,255,0.05)':'#f8fafc',
            color:text, fontSize:13, outline:'none', cursor:'pointer' }}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_CONFIG).map(([k,v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select value={filterProv} onChange={e=>setFilterProv(e.target.value)}
          style={{ padding:'9px 12px', borderRadius:12, border, background: darkMode?'rgba(255,255,255,0.05)':'#f8fafc',
            color:text, fontSize:13, outline:'none', cursor:'pointer' }}>
          <option value="">Todas las provincias</option>
          {provincias.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer',
          padding:'9px 14px', borderRadius:12, border,
          background: filterAog ? 'rgba(239,68,68,0.08)' : (darkMode?'rgba(255,255,255,0.05)':'#f8fafc'),
          color: filterAog ? '#ef4444' : text, fontSize:13, fontWeight:600 }}>
          <input type="checkbox" checked={filterAog} onChange={e=>setFilterAog(e.target.checked)}
            style={{ display:'none' }} />
          ⚡ AOG
        </label>

        <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer',
          padding:'9px 14px', borderRadius:12, border,
          background: filterAnac ? 'rgba(22,163,74,0.08)' : (darkMode?'rgba(255,255,255,0.05)':'#f8fafc'),
          color: filterAnac ? '#16a34a' : text, fontSize:13, fontWeight:600 }}>
          <input type="checkbox" checked={filterAnac} onChange={e=>setFilterAnac(e.target.checked)}
            style={{ display:'none' }} />
          ANAC ✓
        </label>

        {hasFilters && (
          <motion.button
            onClick={() => { setSearch(''); setFilterTipo(''); setFilterProv(''); setFilterAog(false); setFilterAnac(false); }}
            whileTap={{ scale:0.97 }}
            style={{ padding:'9px 14px', borderRadius:12, border, background:'transparent',
              color:sub, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
              display:'flex', alignItems:'center', gap:5 }}>
            <X style={{ width:13, height:13 }} /> Limpiar
          </motion.button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:sub }}>
          <div style={{ width:32, height:32, borderRadius:'50%', border:`3px solid ${sub}`,
            borderTopColor:'#2563eb', animation:'spin 1s linear infinite', margin:'0 auto 12px' }} />
          <p style={{ margin:0, fontSize:13 }}>Cargando directorio...</p>
        </div>
      ) : providers.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:sub }}>
          <p style={{ margin:0, fontSize:24 }}>🔍</p>
          <p style={{ margin:'8px 0 0', fontSize:14 }}>No se encontraron proveedores</p>
        </div>
      ) : hasFilters ? (
        // Vista plana cuando hay filtros activos
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:14, alignItems:'stretch' }}>
          {providers.map(p => (
            <ProviderCard key={p.id} provider={p} darkMode={darkMode} userRole={userRole}
              onVerify={handleVerify}
              onReview={handleReview}
              onEdit={(prov) => { setEditTarget(prov); setShowModal(true); }}
              onDetail={(prov) => setDetailProvider(prov)} />
          ))}
        </div>
      ) : (
        // Vista agrupada por tipo
        tipoOrder.filter(t => grouped[t]?.length > 0).map(tipo => {
          const conf = TIPO_CONFIG[tipo];
          const Icon = conf.icon;
          return (
            <div key={tipo} style={{ marginBottom:28 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:32, height:32, borderRadius:10, background:`${conf.color}15`,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Icon style={{ width:16, height:16, color:conf.color }} />
                </div>
                <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:text }}>
                  {conf.label}
                </h3>
                <span style={{ fontSize:12, color:sub }}>
                  ({grouped[tipo].length})
                </span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:14, alignItems:'stretch' }}>
                {grouped[tipo].map(p => (
                  <ProviderCard key={p.id} provider={p} darkMode={darkMode} userRole={userRole}
                    onVerify={handleVerify}
                    onReview={handleReview}
                    onEdit={(prov) => { setEditTarget(prov); setShowModal(true); }}
                    onDetail={(prov) => setDetailProvider(prov)} />
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Modal detalle */}
      <AnimatePresence>
        {detailProvider && (
          <ProviderDetailModal
            provider={detailProvider}
            darkMode={darkMode}
            userRole={userRole}
            onClose={() => setDetailProvider(null)}
            onEdit={(prov) => { setEditTarget(prov); setShowModal(true); }}
            onVerify={handleVerify}
          />
        )}
      </AnimatePresence>

      {/* Modal crear/editar */}
      <AnimatePresence>
        {showModal && (
          <ProviderModal
            provider={editTarget}
            darkMode={darkMode}
            onSave={handleSaved}
            onClose={() => { setShowModal(false); setEditTarget(null); }}
          />
        )}
      </AnimatePresence>

      {/* Toast cambios guardados */}
      <AnimatePresence>
        {editSaved && (
          <motion.div
            key="edit-toast"
            initial={{ opacity:0, y:-24, scale:0.92 }}
            animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:-16 }}
            transition={{ type:'spring', stiffness:200, damping:22 }}
            style={{
              position:'fixed', top:80, left:'50%', transform:'translateX(-50%)',
              zIndex:300, display:'flex', alignItems:'center', gap:10,
              padding:'12px 20px', borderRadius:20,
              background:'linear-gradient(135deg,#16a34a,#15803d)',
              boxShadow:'0 8px 24px rgba(22,163,74,0.35)',
              whiteSpace:'nowrap', pointerEvents:'none',
            }}>
            <CheckCircle style={{ width:16, height:16, color:'white', flexShrink:0 }} />
            <span style={{ fontSize:14, fontWeight:700, color:'white' }}>
              Cambios guardados correctamente
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default AdminSuppliers;
